/**
 * Dialog which is connected to an element
 * and will show up like a dropdown if
 * device is large enough.
 *
 * @author bgr
 */
import { html } from "lit-html";
import { Base } from "../../shared/components/dialogs/Base";
import * as resize from "../../shared/event/resize-observer";
import { getVersion, isAtLeast, SIZE_MOBILE, SIZE_TABLET } from "../dom/screen";
import { scrollToCompletelyVisible } from "../dom/smoothScroll";

const MARGIN = 20;

export class DropdownDialog extends Base {
	constructor(anchorElement) {
		super();
		this.anchor = anchorElement;
		this.node.classList.add("dialog--dropdown");
		resize.watch(this, async () => {
			this.render();
			await this.updatePosition();
		});
		resize.pause(this);
	}

	getTemplate() {
		return html`
			<div class="dialog-dropdown-arrow"></div>
			${super.getTemplate()}
		`;
	}

	async show(zIndex = null) {
		super.show(zIndex);
		if (getVersion() === SIZE_MOBILE) {
			document.body.style.overflow = "hidden";
		}
		await this.updatePosition();
		resize.resume(this);
	}

	onShow() {
		if (isAtLeast(SIZE_TABLET)) {
			scrollToCompletelyVisible(this.node);
		}
	}

	hide() {
		super.hide();
		this.activateBackgroundScrolling();
		this.anchor.style.zIndex = "";
		resize.pause(this);
	}

	get needed_minimal_height() {
		return 500;
	}

	async updatePosition() {
		if (isAtLeast(SIZE_TABLET)) {
			this.activateBackgroundScrolling();
		} else {
			await scrollToCompletelyVisible(this.anchor, this.needed_minimal_height);
		}

		const anchorRect = this.anchor.getBoundingClientRect();
		const dialogRect = this.node.getBoundingClientRect();

		const margin = isAtLeast(SIZE_TABLET) ? MARGIN : 0;
		const dialogLeft = Math.max(
			margin,
			Math.min(
				document.documentElement.clientWidth - margin - dialogRect.width,
				anchorRect.left + (anchorRect.width - dialogRect.width) / 2,
			),
		);

		this.node.style.top =
			window.pageYOffset + anchorRect.top + anchorRect.height + "px";
		this.node.style.left = dialogLeft + "px";
		this.node.style.setProperty(
			"--dialog-max-height",
			window.innerHeight - anchorRect.top - anchorRect.height + "px",
		);

		const arrowElement = this.node.querySelector(".dialog-dropdown-arrow");
		arrowElement.style.left =
			anchorRect.left - dialogLeft + anchorRect.width / 2 + "px";

		setTimeout(() => {
			this.anchor.style.zIndex = this.node.style.zIndex;
		}, 0);
	}

	activateBackgroundScrolling() {
		document.body.style.overflow = "";
	}
}
